import { Formik, FormikValues } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from '../CreateOrEditCategoryModal/styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import ProjectTextArea from '../../inputs/ProjectTextArea';
import ProjectMultipleSelect from '../../inputs/ProjectMultipleSelect';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { MentorBoard, MentorBoardMember } from '../../../api/types/mentorBoard';
import { MAX_MENTOR_BOARD_MEMBERS, MinimalMentorBoardSchema } from '../../../api/mentorBoard/parser';
import { CreateMentorBoardDto } from '../../../api/typeDtos/mentorBoard';
import { createMentorBoard, updateMentorBoard } from '../../../api/mentorBoard';
import { Author } from '../../../api/types/author';
import { getAllAuthors } from '../../../api/author';
import ProjectSelect from '../../inputs/ProjectSelect';
import { ContentGroup } from '../../../api/types/contentGroup';
import { getCollectionsByAuthorId } from '../../../api/contentGroup';

const initialValues = {
  title: '',
  subtitle: '',
  description: '',
  isActive: true,
  members: [] as MentorBoardMember[],
};

// Boards saved before per-member titles existed only have memberAuthorIds.
const toMemberFields = (board: MentorBoard): MentorBoardMember[] =>
  board.members?.length
    ? board.members.map((member) => ({
        authorId: member.authorId,
        title: member.title ?? '',
        courseContentGroupId: member.courseContentGroupId ?? '',
      }))
    : (board.memberAuthorIds ?? []).map((authorId) => ({
        authorId,
        title: '',
        courseContentGroupId: '',
      }));

// Lists only the collections that belong to the given author; reloads when the author changes.
const MemberCollectionSelect = ({
  authorId,
  value,
  onChange,
  name,
}: {
  authorId: string;
  value?: string;
  onChange: (value: string) => void;
  name: string;
}) => {
  const [collections, setCollections] = useState<ContentGroup[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    setIsLoaded(false);
    setCollections([]);
    getCollectionsByAuthorId(authorId).then((result) => {
      if (isCancelled) {
        return;
      }
      setCollections(result);
      setIsLoaded(true);
      // The saved selection no longer belongs to this author.
      if (value && !result.some((collection) => collection.id === value)) {
        onChange('');
      }
    });
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorId]);

  return (
    <ProjectSelect
      value={isLoaded ? value : undefined}
      processShownLabel={(id) => collections.find((collection) => collection.id === id)?.title ?? id}
      onChangeSelectedItem={(e) => onChange(e.target.value ?? '')}
      onClearValue={() => onChange('')}
      name={name}
      placeholder={isLoaded ? 'Default: first course' : 'Loading collections...'}
      disabled={!isLoaded}>
      {collections.map((collection) => (
        <Components.MenuItem key={collection.id} value={collection.id}>
          {collection.title}
        </Components.MenuItem>
      ))}
    </ProjectSelect>
  );
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: MentorBoard;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const [authors, setAuthors] = useState<Author[]>([]);

  useEffect(() => {
    getAllAuthors().then(setAuthors);
  }, []);

  const authorsById: Record<string, Author> = Object.fromEntries(authors.map((author) => [author.id, author]));

  const boardInitialValues = {
    title: modalState?.title,
    subtitle: modalState?.subtitle,
    description: modalState?.description,
    isActive: modalState?.isActive ?? true,
    members: modalState ? toMemberFields(modalState) : [],
  };

  const onMembersChange = (
    selected: string | string[] | undefined,
    currentMembers: MentorBoardMember[],
    setFieldValue: (field: string, value: MentorBoardMember[]) => void,
  ) => {
    const selectedIds = (typeof selected === 'string' ? selected.split(',') : selected ?? []).slice(
      0,
      MAX_MENTOR_BOARD_MEMBERS,
    );
    // Keep already typed titles for members that stay selected.
    const members = selectedIds.map(
      (authorId) =>
        currentMembers.find((member) => member.authorId === authorId) ?? {
          authorId,
          title: '',
          courseContentGroupId: '',
        },
    );
    setFieldValue('members', members);
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
      // Optional fields are omitted when empty (Firestore rejects undefined values).
      members: (values.members as MentorBoardMember[]).map((member) => ({
        authorId: member.authorId,
        title: (member.title || '').trim(),
        ...(member.courseContentGroupId && { courseContentGroupId: member.courseContentGroupId }),
      })),
    });
  };

  return (
    <Formik
      initialValues={modalState ? boardInitialValues : initialValues}
      validationSchema={MinimalMentorBoardSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched, setFieldValue }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Title</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.title}
                    onChangeText={handleChange}
                    name={'title'}
                    errorMessage={errors.title && touched.title ? errors?.title : undefined}
                    placeholder={'e.g. Start a business'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Subtitle</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.subtitle}
                    onChangeText={handleChange}
                    name={'subtitle'}
                    errorMessage={errors.subtitle && touched.subtitle ? errors?.subtitle : undefined}
                    placeholder={'e.g. From idea to paying customer'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>
                    Members ({values.members.length}/{MAX_MENTOR_BOARD_MEMBERS})
                  </Components.FieldLabel>
                  <ProjectMultipleSelect
                    value={values.members.map((member: MentorBoardMember) => member.authorId)}
                    processShownLabel={(id) => authorsById[id]?.name ?? id}
                    onChangeSelectedItem={(e) => onMembersChange(e.target.value, values.members, setFieldValue)}
                    name={'members'}
                    errorMessages={typeof errors.members === 'string' ? errors.members : undefined}
                    placeholder={`Select up to ${MAX_MENTOR_BOARD_MEMBERS} mentors`}>
                    {authors.map((author) => (
                      <Components.MenuItem
                        key={author.id}
                        value={author.id}
                        disabled={
                          values.members.length >= MAX_MENTOR_BOARD_MEMBERS &&
                          !values.members.some((member: MentorBoardMember) => member.authorId === author.id)
                        }>
                        {author.name}
                      </Components.MenuItem>
                    ))}
                  </ProjectMultipleSelect>
                </Components.FieldWrapper>
                {values.members.map((member: MentorBoardMember, index: number) => {
                  const memberError = Array.isArray(errors.members)
                    ? (errors.members[index] as { title?: string } | undefined)?.title
                    : undefined;
                  const memberTouched = Array.isArray(touched.members)
                    ? (touched.members[index] as { title?: boolean } | undefined)?.title
                    : undefined;
                  return (
                    <Components.FieldWrapper key={member.authorId}>
                      <Components.FieldLabel>
                        {authorsById[member.authorId]?.name ?? member.authorId} - short title
                      </Components.FieldLabel>
                      <ProjectTextField
                        value={member.title}
                        onChangeText={handleChange}
                        name={`members[${index}].title`}
                        errorMessage={memberError && memberTouched ? memberError : undefined}
                        placeholder={'e.g. Clarify your offer'}
                      />
                      <Components.FieldLabel>
                        {authorsById[member.authorId]?.name ?? member.authorId} - course (collection)
                      </Components.FieldLabel>
                      <MemberCollectionSelect
                        authorId={member.authorId}
                        value={member.courseContentGroupId}
                        onChange={(value) => setFieldValue(`members[${index}].courseContentGroupId`, value)}
                        name={`members[${index}].courseContentGroupId`}
                      />
                      <Components.FieldBasic>
                        Optional. Leave empty to use the mentor&apos;s first course.
                      </Components.FieldBasic>
                    </Components.FieldWrapper>
                  );
                })}
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Description</Components.FieldLabel>
                  <ProjectTextArea
                    value={values.description}
                    onChangeText={handleChange}
                    name={'description'}
                    errorMessage={errors.description && touched.description ? errors?.description : undefined}
                    placeholder={
                      'e.g. Go from raw idea to first paying customer with a step-by-step path built for action.'
                    }
                    rows={4}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.CheckboxFieldWrapper>
                  <Components.Checkbox
                    checked={values?.isActive}
                    onChange={(e) => setFieldValue('isActive', e.target.checked)}
                    name={'isActive'}
                  />
                  <Components.CheckboxLabel>Active</Components.CheckboxLabel>
                </Components.CheckboxFieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateOrEditMentorBoardModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { board?: MentorBoard };
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreateMentorBoardDto = {
      title: values?.title,
      subtitle: values?.subtitle,
      description: values?.description,
      isActive: values?.isActive,
      memberAuthorIds: (values?.members ?? []).map((member: MentorBoardMember) => member.authorId),
      members: values?.members ?? [],
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.board?.id
        ? await updateMentorBoard({ ...data, id: modalState?.board?.id })
        : await createMentorBoard(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.board?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a mentor board');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.board} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.board ? `Edit mentor board` : 'Create new mentor board'}
        titleSuffix={modalState?.board ? `(ID: ${modalState?.board?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditMentorBoardModal;
